import { useSyncExternalStore } from 'react';

export type InitialContentSource =
    | 'composer' | 'surface' | 'viewer' | 'render_error' | 'bootstrap_error'
    | 'workspace_error' | 'workspace_empty' | 'onboarding' | 'access_rejected'
    | 'route_changed' | 'unclassified';

export interface InitialContentObservation {
    readonly source: InitialContentSource;
    readonly observedAtMS: number;
    readonly observedMonoMS: number;
}

/** 只有实际登记了内容就绪回调的工作台等待首帧；Builder/DS 等独立入口不继承 Workspace 的等待。 */
export function isInitialContentRoute(pathname: string): boolean {
    return /^\/workspace(?:\/[^/]+)?\/?$/.test(pathname) || /^\/project\/[^/]+\/?$/.test(pathname);
}

function createInitialContent() {
    // 只延后直接打开工作台的首次文档；从已就绪文档软导航进来不重新关闭。
    let ready = typeof window === 'undefined' || !isInitialContentRoute(window.location.pathname);
    let observation: InitialContentObservation | null = null;
    const listeners = new Set<() => void>();
    let complete: (() => void) | undefined;
    const pending = ready ? Promise.resolve() : new Promise<void>((resolve) => { complete = resolve; });

    return {
        snapshot: () => ready,
        observation: () => observation,
        wait: () => pending,
        subscribe(listener: () => void) {
            listeners.add(listener);
            return () => { listeners.delete(listener); };
        },
        complete(source: InitialContentSource = 'unclassified') {
            if (ready) return;
            observation = Object.freeze({ source, observedAtMS: Date.now(), observedMonoMS: performance.now() });
            ready = true;
            complete?.();
            listeners.forEach((listener) => listener());
            listeners.clear();
        },
    };
}

// 不存用户或业务数据：每个浏览器文档只从等待变为就绪一次，无调度器、超时和重试状态。
const initialContent = createInitialContent();
const getServerSnapshot = () => false;

/** SDK 下载复用这一个等待；授权期事件仍由各 SDK 已有队列保存。 */
export const waitForInitialContent = initialContent.wait;
/** 核心内容已能交互，或错误/空态/Onboarding 已显示。只完成首次文档，不跟随 Tab 重置。 */
export const markInitialContentReady = initialContent.complete;

/** 原等待释放的来源事实；错误页也能释放等待，因此不能只按 ready 判成功。 */
export const getInitialContentObservation = initialContent.observation;

/** 页面侧的非关键副作用跟随同一个就绪信号，不重建根 SDK 实例。 */
export function useInitialContentReady(): boolean {
    return useSyncExternalStore(initialContent.subscribe, initialContent.snapshot, getServerSnapshot);
}

/** 只读取当前文档首次来源，不因软导航、账号切换或后续重试改写。 */
export function useInitialContentObservation(): InitialContentObservation | null {
    return useSyncExternalStore(initialContent.subscribe, initialContent.observation, () => null);
}
